import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import { get, post, patch, del } from '../lib/api'
import EmployeeReviews from './EmployeeReviews'
import MyPlanner from './MyPlanner'
import MyPDRs from './MyPDRs'
import MyMeetings from './MyMeetings'
import NotificationBell from '../components/NotificationBell'
import CalendarView from '../components/CalendarView'
import ExpenseLog from '../components/ExpenseLog'
import Jobs from '../components/Jobs'
import NotificationSettings from '../components/NotificationSettings'
import Modal from '../components/Modal'

const LIGHT = {
  bg: '#F8F9FA', sidebar: '#1E293B', white: '#FFFFFF',
  primary: '#4F46E5', primary2: '#6366F1',
  text: '#1E293B', textSub: '#475569', textMuted: '#9CA3AF',
  border: '#E5E7EB', success: '#10B981', amber: '#F59E0B',
  danger: '#EF4444', purple: '#7C3AED',
  card: '#FFFFFF', cardBorder: '#E5E7EB',
  input: '#F8F9FA', inputBorder: '#E5E7EB', inputText: '#1E293B',
  shadow: '0 1px 3px rgba(0,0,0,0.08)', shadowMd: '0 4px 6px rgba(0,0,0,0.07)',
  dark: false,
}
const DARK = {
  bg: '#0F172A', sidebar: '#0A0A0F', white: '#1E293B',
  primary: '#6366F1', primary2: '#818CF8',
  text: '#F1F5F9', textSub: '#94A3B8', textMuted: '#64748B',
  border: '#334155', success: '#10B981', amber: '#F59E0B',
  danger: '#EF4444', purple: '#A78BFA',
  card: '#1E293B', cardBorder: '#334155',
  input: '#0F172A', inputBorder: '#334155', inputText: '#F1F5F9',
  shadow: '0 1px 3px rgba(0,0,0,0.3)', shadowMd: '0 4px 6px rgba(0,0,0,0.3)',
  dark: true,
}

const NAV = [
  { id: 'dashboard', icon: '🏠', label: 'Dashboard' },
  { id: 'pdrs',      icon: '📋', label: 'My PDRs' },
  { id: 'checkin',   icon: '✅', label: 'Check-in / Checkout' },
  { id: 'meetings',  icon: '📅', label: 'My Meetings' },
  { id: 'calendar',  icon: '🗓️', label: 'Calendar' },
  { id: 'jobs',      icon: '🧩', label: 'Assigned Jobs' },
  { id: 'reviews',   icon: '⭐', label: 'My Reviews' },
  { id: 'expenses',  icon: '🧾', label: 'Expense Log' },
  { id: 'planner',   icon: '📝', label: 'My Planner' },
  { id: 'profile',   icon: '👤', label: 'My Profile' },
]

const inputStyle = (t) => ({
  display: 'block', width: '100%', padding: '11px 14px',
  background: t.input, border: `1px solid ${t.inputBorder}`,
  borderRadius: 8, fontSize: 14, color: t.inputText,
  fontFamily: 'Inter, system-ui', boxSizing: 'border-box', outline: 'none',
})
const labelStyle = (t) => ({ display: 'block', fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 6 })

function moodEmoji(m) { return { great: '😄', good: '🙂', okay: '😐', tough: '😔' }[m] || '😐' }
function moodColor(m) { return { great: '#059669', good: '#4F46E5', okay: '#D97706', tough: '#EF4444' }[m] || '#64748B' }
function moodBg(m)    { return { great: '#ECFDF5', good: '#EFF6FF', okay: '#FFFBEB', tough: '#FEF2F2' }[m] || '#F8F9FA' }
function getTime()    { const h = new Date().getHours(); return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening' }
function getLast7Days() {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i))
    return { date: d.toISOString().split('T')[0], label: d.toLocaleDateString('en-US', { weekday: 'short' }) }
  })
}

export default function EmployeeDashboard() {
  const { profile, signOut } = useAuth()
  const navigate = useNavigate()
  const [dark, setDark] = useState(() => localStorage.getItem('emp_dark') === 'true')
  const [nav, setNav] = useState('dashboard')
  const [checkinOpen, setCheckinOpen] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [checkIns, setCheckIns] = useState([])
  const [pdrs, setPdrs] = useState([])
  const [meetings, setMeetings] = useState([])
  const [reviews, setReviews] = useState([])
  const [reviewPreselect, setReviewPreselect] = useState(null)
  const [empProfile, setEmpProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const t = dark ? DARK : LIGHT

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    setLoading(true)
    try {
      const [ciR, pR, mR, rR, meR] = await Promise.all([
        get('/check-ins'),
        get('/pdrs'),
        get('/meetings'),
        get('/reviews'),
        get('/auth/me'),
      ])
      setCheckIns(ciR.checkIns || [])
      setPdrs((pR.pdrs || []).map((p) => ({ ...p, pdr_goals: p.goals || [] })))
      setMeetings(mR.meetings || [])
      setReviews(rR.reviews || [])
      if (meR.user) setEmpProfile(meR.user)
    } catch (e) {
      console.error('Fetch error:', e.message)
    }
    setLoading(false)
  }

  const today = new Date().toISOString().split('T')[0]
  const todayRecord = checkIns.find(c => c.date === today)
  const todayCheckedIn  = !!todayRecord?.checkin_submitted_at
  const todayCheckedOut = !!todayRecord?.checkout_submitted_at
  const todayDone = todayCheckedIn
  const activePdr = pdrs.find(p => p.status === 'active')
  const pendingCount = pdrs.filter(p => p.status === 'pending_approval').length
  const meetingPendingCount = meetings.filter(m =>
    !(m.rsvp_statuses || {})[profile.id] ||
    (m.rsvp_statuses || {})[profile.id] === 'pending'
  ).length
  const rejectedPdrCount    = pdrs.filter(p => p.status === 'rejected').length
  // Notification counts for sidebar badges
  const reviewNotifCount    = reviews.filter(r => r.status === 'employer_reviewed').length
  const pdrNotifCount       = pdrs.filter(p => p.status === 'rejected' || p.status === 'active').length
  const meetingNotifCount   = meetings.filter(m => {
    const rsvp = (m.rsvp_statuses || {})[profile.id]
    return !rsvp || rsvp === 'pending'
  }).length

  const P = {
    t, profile: empProfile || profile, checkIns, pdrs, meetings, reviews,
    onRefresh: fetchAll, navigate, todayDone, todayCheckedIn, todayCheckedOut,
    setNav, setReviewPreselect,
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: t.bg, fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* SIDEBAR */}
      <div className={`eh-sidebar${sidebarOpen ? ' open' : ''}`} style={{ width: 250, background: t.sidebar, display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, bottom: 0, zIndex: 50, overflowY: 'auto' }}>

        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 9, background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ color: '#fff', fontWeight: 800, fontSize: 13 }}>EH</span>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Employee Hive</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{profile?.organizations?.name}</div>
            </div>
          </div>
        </div>

        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <ProfileAvatar profile={empProfile || profile} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{empProfile?.full_name || profile?.full_name}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{empProfile?.position || 'Employee'}</div>
          </div>
        </div>

        <nav style={{ flex: 1, padding: '1rem 0.75rem' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.12em', padding: '0 14px', marginBottom: 10, textTransform: 'uppercase' }}>Navigation</div>
          {NAV.map(item => {
            const active = nav === item.id
            return (
              <div key={item.id} className="eh-nav-item" onClick={() => { setNav(item.id); setSidebarOpen(false) }} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px', borderRadius: 10, cursor: 'pointer', marginBottom: 3,
                background: active ? 'rgba(99,102,241,0.25)' : 'transparent',
                color: active ? '#fff' : 'rgba(255,255,255,0.85)',
                fontSize: 14, fontWeight: active ? 700 : 500,
                borderLeft: active ? '3px solid #818CF8' : '3px solid transparent',
                transition: 'all .15s',
              }}>
                <span style={{ fontSize: 18, width: 24, textAlign: 'center', flexShrink: 0, lineHeight: 1 }}>{item.icon}</span>
                <span style={{ flex: 1, fontSize: 13, letterSpacing: '0.01em' }}>{item.label}</span>
                {item.id === 'checkin' && !todayCheckedOut && (
                  <span style={{ fontSize: 10, background: t.amber, color: '#fff', padding: '2px 7px', borderRadius: 99, fontWeight: 700 }}>
                    {todayCheckedIn ? 'Check out' : 'Due'}
                  </span>
                )}
                {item.id === 'pdrs' && pendingCount > 0 && (
                  <span style={{ fontSize: 10, background: t.amber, color: '#fff', padding: '2px 7px', borderRadius: 99, fontWeight: 700 }}>{pendingCount}</span>
                )}
                {item.id === 'meetings' && meetingPendingCount > 0 && (
                  <span style={{ fontSize: 10, background: '#4F46E5', color: '#fff', padding: '2px 7px', borderRadius: 99, fontWeight: 700 }}>
                    {meetingPendingCount}
                  </span>
                )}
                {item.id === 'pdrs' && rejectedPdrCount > 0 && (
                  <span style={{ fontSize: 10, background: '#EF4444', color: '#fff', padding: '2px 7px', borderRadius: 99, fontWeight: 700 }}>
                    {rejectedPdrCount}
                  </span>
                )}
                {item.id === 'reviews' && reviewNotifCount > 0 && (
                  <span style={{ fontSize: 10, background: '#7C3AED', color: '#fff', padding: '2px 7px', borderRadius: 99, fontWeight: 700 }}>
                    {reviewNotifCount}
                  </span>
                )}
              </div>
            )
          })}

          {activePdr && (
            <div style={{ margin: '1rem 0 0', padding: '10px 12px', background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#A5B4FC', marginBottom: 4, letterSpacing: '0.06em' }}>ACTIVE PDR</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', marginBottom: 6 }}>{activePdr.title}</div>
              <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 99, height: 4 }}>
                <div style={{ width: `${Math.round((activePdr.pdr_goals || []).reduce((s, g) => s + g.progress, 0) / Math.max((activePdr.pdr_goals || []).length, 1))}%`, height: '100%', background: '#6366F1', borderRadius: 99 }} />
              </div>
            </div>
          )}
        </nav>

        <div style={{ padding: '1rem 0.75rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          {/* Dark mode toggle */}
          <div onClick={() => { const d = !dark; setDark(d); localStorage.setItem('emp_dark', d) }}
            style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 12px', borderRadius:8, cursor:'pointer', marginBottom:4,
              background: dark ? 'rgba(255,255,255,0.08)' : 'rgba(99,102,241,0.15)',
              border: '1px solid rgba(255,255,255,0.1)' }}>
            <span style={{ fontSize:16 }}>{dark ? '☀️' : '🌙'}</span>
            <span style={{ fontSize:13, fontWeight:600, color:'#fff' }}>{dark ? 'Light Mode' : 'Dark Mode'}</span>
            <div style={{ marginLeft:'auto', width:32, height:18, borderRadius:99, background: dark ? '#6366F1' : 'rgba(255,255,255,0.2)', position:'relative', transition:'background .2s' }}>
              <div style={{ position:'absolute', top:3, left: dark ? 16 : 3, width:12, height:12, borderRadius:'50%', background:'#fff', transition:'left .2s' }} />
            </div>
          </div>
          <div onClick={signOut} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
            <span>←</span> Sign out
          </div>
        </div>
      </div>

      {/* Mobile overlay behind the open sidebar */}
      <div className={`eh-sidebar-overlay${sidebarOpen ? ' show' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* MAIN */}
      <div className="eh-main" style={{ marginLeft: 250, flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

        <div className="eh-topbar" style={{ background: t.white, borderBottom: `1px solid ${t.border}`, padding: '0 2rem', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 40, boxShadow: t.shadow }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="eh-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Menu"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 22, color: t.text, padding: 4, alignItems: 'center', justifyContent: 'center' }}>☰</button>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: t.text, margin: 0, letterSpacing: '-0.3px' }}>
              {NAV.find(n => n.id === nav)?.label}
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <NotificationBell t={t} onNavigate={setNav} />
            <button className="eh-hide-mobile" onClick={() => { const d = !dark; setDark(d); localStorage.setItem('emp_dark', d) }}
              style={{ background: dark ? 'rgba(255,255,255,0.1)' : '#F1F5F9', border: `1px solid ${t.border}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 14, color: t.text, fontFamily: 'Inter,system-ui', fontWeight: 600 }}>
              {dark ? '☀️ Light' : '🌙 Dark'}
            </button>
            <span className="eh-hide-mobile" style={{ fontSize: 13, color: t.textMuted }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </span>
            <div className="eh-hide-mobile" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 99, background: todayCheckedOut ? '#ECFDF5' : todayCheckedIn ? '#EFF6FF' : '#FEF3C7', border: `1px solid ${todayCheckedOut ? '#A7F3D0' : todayCheckedIn ? '#BFDBFE' : '#FDE68A'}` }}>
              <span style={{ fontSize: 11 }}>{todayCheckedOut ? '✅' : todayCheckedIn ? '🌙' : '⏰'}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: todayCheckedOut ? '#059669' : todayCheckedIn ? '#4F46E5' : '#D97706' }}>
                {todayCheckedOut ? 'Checked out' : todayCheckedIn ? 'Checked in' : 'Check-in due'}
              </span>
            </div>
            <div onClick={() => setNav('profile')} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px', borderRadius: 10, background: t.bg, border: `1px solid ${t.border}`, cursor: 'pointer' }}>
              <ProfileAvatar profile={empProfile || profile} size={28} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.text, lineHeight: 1.2 }}>{empProfile?.full_name || profile?.full_name}</div>
                <div style={{ fontSize: 11, color: t.textMuted }}>{empProfile?.position || 'Employee'}</div>
              </div>
            </div>
          </div>
        </div>

        {!loading && !todayCheckedOut && nav !== 'checkin' && (
          <div onClick={() => setCheckinOpen(true)} style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', padding: '12px 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>⏰</span>
              <div>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{todayCheckedIn ? 'Evening check-out pending' : 'Morning check-in pending'}</span>
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', marginLeft: 10 }}>Takes less than 2 minutes</span>
              </div>
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', background: 'rgba(255,255,255,0.2)', padding: '6px 14px', borderRadius: 8 }}>{todayCheckedIn ? 'Check out now →' : 'Check in now →'}</span>
          </div>
        )}

        <div key={nav} className="eh-view eh-content" style={{ flex: 1, padding: '2rem', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
              <p style={{ color: t.textMuted }}>Loading…</p>
            </div>
          ) : (
            <>
              {nav === 'dashboard' && <EmpOverview {...P} />}
              {nav === 'checkin'   && <CheckIn {...P} />}
              {nav === 'planner'  && <MyPlanner t={t} profile={empProfile || profile} />}
              {nav === 'meetings' && (
                <>
                  {meetingNotifCount > 0 && (
                    <div style={{ background:'#EEF2FF', border:'1px solid #C7D2FE', borderRadius:12, padding:'12px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
                      <span style={{ fontSize:20 }}>🗓</span>
                      <div>
                        <div style={{ fontSize:14, fontWeight:700, color:'#4F46E5' }}>{meetingNotifCount} meeting{meetingNotifCount>1?'s':''} awaiting your RSVP</div>
                        <div style={{ fontSize:12, color:'#6366F1' }}>Please respond so your manager can plan accordingly.</div>
                      </div>
                    </div>
                  )}
                  <MyMeetings t={t} profile={empProfile || profile} onRefresh={fetchAll} />
                </>
              )}
              {nav === 'pdrs' && (
                <>
                  {rejectedPdrCount > 0 && (
                    <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:12, padding:'12px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
                      <span style={{ fontSize:20 }}>❌</span>
                      <div>
                        <div style={{ fontSize:14, fontWeight:700, color:'#EF4444' }}>{rejectedPdrCount} PDR{rejectedPdrCount>1?'s':''} rejected — action required</div>
                        <div style={{ fontSize:12, color:'#DC2626' }}>Review the feedback and resubmit your PDR to continue.</div>
                      </div>
                    </div>
                  )}
                  <MyPDRs {...P} />
                </>
              )}
              {nav === 'reviews' && (
                <>
                  {reviewNotifCount > 0 && (
                    <div style={{ background:'#F5F3FF', border:'1px solid #DDD6FE', borderRadius:12, padding:'12px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
                      <span style={{ fontSize:20 }}>✍️</span>
                      <div>
                        <div style={{ fontSize:14, fontWeight:700, color:'#7C3AED' }}>{reviewNotifCount} review{reviewNotifCount>1?'s':''} ready for your sign-off</div>
                        <div style={{ fontSize:12, color:'#6D28D9' }}>Your manager has completed their assessment — review scores and sign off.</div>
                      </div>
                    </div>
                  )}
                  <EmployeeReviews t={t} profile={empProfile || profile} pdrs={pdrs} preselectedPdrId={reviewPreselect} onClearPreselect={() => setReviewPreselect(null)} onRefresh={fetchAll} />
                </>
              )}
              {nav === 'calendar'  && <CalendarView t={t} />}
              {nav === 'jobs'      && <Jobs t={t} isEmployer={false} />}
              {nav === 'expenses'  && <ExpenseLog t={t} isEmployer={false} />}
              {nav === 'profile'   && <MyProfile {...P} onRefreshProfile={fetchAll} />}
            </>
          )}
        </div>
      </div>

      <Modal open={checkinOpen} onClose={() => setCheckinOpen(false)} t={t} title="Check-in / Checkout" width={680}>
        <CheckIn {...P} onRefresh={() => { fetchAll(); setCheckinOpen(false) }} />
      </Modal>
    </div>
  )
}

function ProfileAvatar({ profile, size = 40 }) {
  if (profile?.avatar_url) return (
    <img src={profile.avatar_url} alt={profile?.full_name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid rgba(255,255,255,0.1)' }} />
  )
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.35, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
      {profile?.full_name?.charAt(0)?.toUpperCase()}
    </div>
  )
}

function EmpOverview({ t, profile, checkIns, pdrs, meetings, todayDone, setNav, navigate }) {
  const activePdrs = pdrs.filter(p => p.status === 'active')
  const allGoals = pdrs.flatMap(p => p.pdr_goals || [])
  const avgProgress = allGoals.length ? Math.round(allGoals.reduce((s, g) => s + g.progress, 0) / allGoals.length) : 0
  const last7 = getLast7Days()

  return (
    <div>
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>
          Good {getTime()}, {profile?.full_name?.split(' ')[0]} 👋
        </h1>
        <p style={{ fontSize: 15, color: t.textMuted, margin: 0 }}>Here's your performance overview</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: '1.75rem' }}>
        {[
          { label: 'Active PDRs',      value: activePdrs.length, color: '#4F46E5', icon: '📋', sub: 'review cycles' },
          { label: 'Check-in/Checkout', value: checkIns.length,   color: '#10B981', icon: '✅', sub: 'days logged' },
          { label: 'Goal Progress',    value: `${avgProgress}%`, color: '#F59E0B', icon: '🎯', sub: 'avg across goals' },
          { label: 'Meetings',         value: meetings.length,   color: '#7C3AED', icon: '🗓', sub: 'recorded' },
        ].map(s => (
          <div key={s.label} style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.25rem', boxShadow: t.shadow }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: `${s.color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{s.icon}</div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{s.label}</div>
            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: '1.5rem' }}>
        <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.5rem', boxShadow: t.shadow }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: 0 }}>Active PDR Cycles</h3>
            <button onClick={() => setNav('pdrs')} style={{ fontSize: 12, color: '#4F46E5', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontFamily: 'Inter, system-ui' }}>View all →</button>
          </div>
          {activePdrs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
              <p style={{ fontSize: 13, color: t.textMuted, marginBottom: '1rem' }}>No active PDR yet</p>
              <button onClick={() => navigate('/pdr/new')} style={{ padding: '8px 18px', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'Inter, system-ui' }}>
                + Create PDR
              </button>
            </div>
          ) : activePdrs.map(pdr => {
            const goals = pdr.pdr_goals || []
            const avg = goals.length ? Math.round(goals.reduce((s, g) => s + g.progress, 0) / goals.length) : 0
            return (
              <div key={pdr.id} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{pdr.title}</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: '#4F46E5' }}>{avg}%</span>
                </div>
                <div style={{ background: t.border, borderRadius: 99, height: 8, marginBottom: 4 }}>
                  <div style={{ width: `${avg}%`, height: '100%', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', borderRadius: 99 }} />
                </div>
                <div style={{ fontSize: 11, color: t.textMuted }}>{goals.length} goals · ends {pdr.end_date}</div>
              </div>
            )
          })}
        </div>

        <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.5rem', boxShadow: t.shadow }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: '0 0 1.25rem' }}>Check-ins this week</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 80, marginBottom: '1rem' }}>
            {last7.map((day, i) => {
              const has = checkIns.some(c => c.date === day.date)
              const isToday = day.date === new Date().toISOString().split('T')[0]
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: '100%', height: 54, borderRadius: '6px 6px 0 0', background: has ? 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)' : isToday ? `${t.amber}30` : t.border, display: 'flex', alignItems: 'center', justifyContent: 'center', border: isToday && !has ? `1px dashed ${t.amber}` : 'none' }}>
                    {has && <span style={{ fontSize: 14, color: '#fff' }}>✓</span>}
                    {isToday && !has && <span style={{ fontSize: 12, color: t.amber }}>!</span>}
                  </div>
                  <span style={{ fontSize: 10, color: isToday ? '#4F46E5' : t.textMuted, fontWeight: isToday ? 700 : 400 }}>{day.label}</span>
                </div>
              )
            })}
          </div>
          <div style={{ fontSize: 13, color: t.textSub }}>
            <strong style={{ color: t.text }}>{checkIns.filter(c => last7.some(d => d.date === c.date)).length}</strong> of 7 days this week
          </div>
        </div>
      </div>

      <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.5rem', boxShadow: t.shadow }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: 0 }}>Recent check-ins</h3>
          <button onClick={() => setNav('checkin')} style={{ fontSize: 12, color: '#4F46E5', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontFamily: 'Inter, system-ui' }}>
            {todayDone ? 'View' : '+ Check in today'}
          </button>
        </div>
        {checkIns.length === 0 ? (
          <p style={{ fontSize: 13, color: t.textMuted, textAlign: 'center', padding: '1rem 0' }}>No check-ins yet.</p>
        ) : checkIns.slice(0, 4).map(c => (
          <div key={c.id} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: `1px solid ${t.border}` }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: moodBg(c.mood), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
              {moodEmoji(c.mood)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{c.date}</span>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: moodBg(c.mood), color: moodColor(c.mood), textTransform: 'capitalize' }}>{c.mood}</span>
              </div>
              <p style={{ fontSize: 13, color: t.textSub, margin: 0, lineHeight: 1.5 }}>{c.done_today?.slice(0, 100)}{c.done_today?.length > 100 ? '…' : ''}</p>
              {c.blockers && <p style={{ fontSize: 12, color: t.textMuted, margin: '4px 0 0' }}>⚠️ {c.blockers}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const MOOD_OPTIONS = [
  { value: 'great', emoji: '😄', label: 'Great', color: '#059669', bg: '#ECFDF5' },
  { value: 'good',  emoji: '🙂', label: 'Good',  color: '#4F46E5', bg: '#EFF6FF' },
  { value: 'okay',  emoji: '😐', label: 'Okay',  color: '#D97706', bg: '#FFFBEB' },
  { value: 'tough', emoji: '😔', label: 'Tough', color: '#EF4444', bg: '#FEF2F2' },
]

function formatTime(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function MoodPicker({ t, value, onChange }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
      {MOOD_OPTIONS.map(m => (
        <div key={m.value} onClick={() => onChange(m.value)}
          style={{ padding: '14px 8px', borderRadius: 10, cursor: 'pointer', textAlign: 'center', border: `2px solid ${value === m.value ? m.color : t.cardBorder}`, background: value === m.value ? m.bg : t.card, transition: 'all .15s' }}>
          <div style={{ fontSize: 26, marginBottom: 6 }}>{m.emoji}</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: value === m.value ? m.color : t.textMuted }}>{m.label}</div>
        </div>
      ))}
    </div>
  )
}

function CheckinSummary({ t, record, goal }) {
  return (
    <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.5rem', boxShadow: t.shadow, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${t.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 22 }}>{moodEmoji(record.checkin_mood)}</span>
          <span style={{ fontSize: 13, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: moodBg(record.checkin_mood), color: moodColor(record.checkin_mood), textTransform: 'capitalize' }}>{record.checkin_mood}</span>
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: t.textMuted }}>Checked in at {formatTime(record.checkin_submitted_at)}</span>
      </div>
      {goal && (
        <div style={{ fontSize: 12, fontWeight: 600, color: '#4F46E5', background: '#4F46E514', padding: '4px 10px', borderRadius: 99, display: 'inline-block', marginBottom: 10 }}>
          🎯 Goal {goal.goal_number}: {goal.title}
        </div>
      )}
      <p style={{ fontSize: 14, color: t.text, margin: '0 0 10px', lineHeight: 1.6 }}><strong>Plan for today:</strong> {record.plan_today}</p>
      {record.challenges && <p style={{ fontSize: 14, color: t.textSub, margin: 0, lineHeight: 1.6 }}><strong>Anticipated challenges:</strong> {record.challenges}</p>}
    </div>
  )
}

function CheckoutSummary({ t, record }) {
  return (
    <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.5rem', boxShadow: t.shadow }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${t.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 22 }}>{moodEmoji(record.mood)}</span>
          <span style={{ fontSize: 13, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: moodBg(record.mood), color: moodColor(record.mood), textTransform: 'capitalize' }}>{record.mood}</span>
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: t.textMuted }}>Checked out at {formatTime(record.checkout_submitted_at)}</span>
      </div>
      <p style={{ fontSize: 14, color: t.text, margin: '0 0 10px', lineHeight: 1.6 }}><strong>What I did:</strong> {record.done_today}</p>
      {record.blockers && <p style={{ fontSize: 14, color: t.textSub, margin: 0, lineHeight: 1.6 }}><strong>Blockers:</strong> {record.blockers}</p>}
    </div>
  )
}

function CheckInSidebar({ t, record, activeGoals }) {
  const checkedIn  = !!record?.checkin_submitted_at
  const checkedOut = !!record?.checkout_submitted_at
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Today's status */}
      <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.5rem', boxShadow: t.shadow }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: '0 0 1.25rem' }}>Today's status</h3>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: checkedIn ? '#ECFDF5' : '#FEF3C7', border: `2px solid ${checkedIn ? '#A7F3D0' : '#FDE68A'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
            {checkedIn ? '✅' : '⏰'}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>Check-in</div>
            <div style={{ fontSize: 12, color: t.textMuted }}>{checkedIn ? `Done at ${formatTime(record.checkin_submitted_at)}` : 'Pending'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: checkedOut ? '#ECFDF5' : checkedIn ? '#FEF3C7' : t.dark ? 'rgba(255,255,255,0.06)' : '#F1F5F9', border: `2px solid ${checkedOut ? '#A7F3D0' : checkedIn ? '#FDE68A' : t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
            {checkedOut ? '✅' : checkedIn ? '⏰' : '—'}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>Check-out</div>
            <div style={{ fontSize: 12, color: t.textMuted }}>
              {checkedOut ? `Done at ${formatTime(record.checkout_submitted_at)}` : checkedIn ? 'Pending' : 'Available after check-in'}
            </div>
          </div>
        </div>
      </div>

      {/* Active goals — grows to fill remaining height */}
      {activeGoals.length > 0 && (
        <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.5rem', boxShadow: t.shadow, flex: 1 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: '0 0 1.25rem' }}>Your active goals</h3>
          {activeGoals.map(g => {
            const prog = g.progress || 0
            const col = prog >= 100 ? '#059669' : prog >= 60 ? '#4F46E5' : prog >= 30 ? '#D97706' : '#EF4444'
            return (
              <div key={g.id} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: t.text, flex: 1, marginRight: 8 }}>Goal {g.goal_number}: {g.title}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#4F46E5', flexShrink: 0 }}>{g.weightage}%</span>
                </div>
                <div style={{ background: t.border, borderRadius: 99, height: 7, marginBottom: 4 }}>
                  <div style={{ width: `${prog}%`, height: '100%', background: `linear-gradient(90deg, ${col}, ${col}cc)`, borderRadius: 99, transition: 'width .4s' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 10, color: t.textMuted }}>Progress</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: col }}>{prog}%</span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function CheckIn({ t, checkIns, pdrs, profile, onRefresh }) {
  const today = new Date().toISOString().split('T')[0]
  const todayRecord = checkIns.find(c => c.date === today)
  const activePdr   = pdrs.find(p => p.status === 'active')
  const activeGoals = activePdr?.pdr_goals || []
  const linkedGoal  = activeGoals.find(g => g.id === todayRecord?.goal_id)

  const [checkinForm,  setCheckinForm]  = useState({ goal_id: '', plan_today: '', challenges: '', checkin_mood: '' })
  const [checkoutForm, setCheckoutForm] = useState({ done_today: '', blockers: '', mood: '' })
  const [loadingIn,  setLoadingIn]  = useState(false)
  const [loadingOut, setLoadingOut] = useState(false)

  function handleCheckinChange(e)  { const {name,value}=e.target; setCheckinForm(prev=>({...prev,[name]:value})) }
  function handleCheckoutChange(e) { const {name,value}=e.target; setCheckoutForm(prev=>({...prev,[name]:value})) }

  async function submitCheckin(e) {
    e.preventDefault()
    if (!checkinForm.checkin_mood) return alert('Please select how you are feeling')
    if (!checkinForm.plan_today.trim()) return alert("Please describe what you're planning to do today")
    setLoadingIn(true)
    let error = null
    try {
      await post('/check-ins', {
        date: today,
        goal_id: checkinForm.goal_id || null, plan_today: checkinForm.plan_today,
        challenges: checkinForm.challenges, checkin_mood: checkinForm.checkin_mood,
        checkin_submitted_at: new Date().toISOString(),
      })
    } catch (e) { error = e }
    if (error) alert(error.message)
    else onRefresh()
    setLoadingIn(false)
  }

  async function submitCheckout(e) {
    e.preventDefault()
    if (!checkoutForm.mood) return alert('Please select how you are feeling')
    if (!checkoutForm.done_today.trim()) return alert('Please describe what you accomplished today')
    setLoadingOut(true)
    let error = null
    try {
      await patch(`/check-ins/${todayRecord.id}`, {
        done_today: checkoutForm.done_today, blockers: checkoutForm.blockers,
        mood: checkoutForm.mood, checkout_submitted_at: new Date().toISOString(),
      })
    } catch (e) { error = e }
    if (error) alert(error.message)
    else onRefresh()
    setLoadingOut(false)
  }

  const IS = inputStyle(t)
  const LS = labelStyle(t)
  const checkedIn  = !!todayRecord?.checkin_submitted_at
  const checkedOut = !!todayRecord?.checkout_submitted_at

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>Check-in / Check-out</h1>
      <p style={{ fontSize: 14, color: t.textMuted, margin: '0 0 1.5rem' }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
        <div>
          {!checkedIn && (
            <form onSubmit={submitCheckin}>
              <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.75rem', boxShadow: t.shadow }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: '0 0 1.25rem' }}>🌅 Morning Check-in</h3>
                {activeGoals.length > 0 && (
                  <div style={{ marginBottom: '1.25rem', padding: '14px', background: t.dark ? 'rgba(99,102,241,0.1)' : '#F0F0FF', borderRadius: 10, border: `1px solid #4F46E520` }}>
                    <label style={{ ...LS, color: '#4F46E5' }}>🎯 Which goal does today relate to? (optional)</label>
                    <select name="goal_id" value={checkinForm.goal_id} onChange={handleCheckinChange} style={{ ...IS, background: t.input }}>
                      <option value="">— Select a goal —</option>
                      {activeGoals.map(g => <option key={g.id} value={g.id}>Goal {g.goal_number}: {g.title} ({g.weightage}% weight)</option>)}
                    </select>
                  </div>
                )}
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={LS}>What are you planning to do today? *</label>
                  <textarea name="plan_today" value={checkinForm.plan_today} onChange={handleCheckinChange} placeholder="Outline your key tasks and priorities for today..." required rows={4} style={{ ...IS, resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={LS}>Any challenges you're anticipating?</label>
                  <textarea name="challenges" value={checkinForm.challenges} onChange={handleCheckinChange} placeholder="Leave blank if none..." rows={2} style={{ ...IS, resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: '1.75rem' }}>
                  <label style={LS}>How are you feeling today? *</label>
                  <MoodPicker t={t} value={checkinForm.checkin_mood} onChange={v => setCheckinForm(prev => ({ ...prev, checkin_mood: v }))} />
                </div>
                <button type="submit" disabled={loadingIn} style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, system-ui', boxShadow: '0 2px 8px rgba(79,70,229,0.3)' }}>
                  {loadingIn ? 'Submitting…' : 'Submit check-in →'}
                </button>
              </div>
            </form>
          )}
          {checkedIn && <CheckinSummary t={t} record={todayRecord} goal={linkedGoal} />}
          {checkedIn && !checkedOut && (
            <form onSubmit={submitCheckout}>
              <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.75rem', boxShadow: t.shadow }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: '0 0 1.25rem' }}>🌙 Evening Check-out</h3>
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={LS}>What did you accomplish today? *</label>
                  <textarea name="done_today" value={checkoutForm.done_today} onChange={handleCheckoutChange} placeholder="Describe what you actually got done..." required rows={4} style={{ ...IS, resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={LS}>Any blockers or challenges you faced?</label>
                  <textarea name="blockers" value={checkoutForm.blockers} onChange={handleCheckoutChange} placeholder="Leave blank if none..." rows={2} style={{ ...IS, resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: '1.75rem' }}>
                  <label style={LS}>How are you feeling now? *</label>
                  <MoodPicker t={t} value={checkoutForm.mood} onChange={v => setCheckoutForm(prev => ({ ...prev, mood: v }))} />
                </div>
                <button type="submit" disabled={loadingOut} style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, system-ui', boxShadow: '0 2px 8px rgba(79,70,229,0.3)' }}>
                  {loadingOut ? 'Submitting…' : 'Submit check-out →'}
                </button>
              </div>
            </form>
          )}
          {checkedOut && <CheckoutSummary t={t} record={todayRecord} />}
          {checkedIn && checkedOut && (
            <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 14, padding: '1.5rem', marginTop: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🎉</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#059669' }}>You're all set for today!</div>
              <div style={{ fontSize: 13, color: '#059669' }}>See you tomorrow morning.</div>
            </div>
          )}
        </div>
        <CheckInSidebar t={t} record={todayRecord} activeGoals={activeGoals} />
      </div>
    </div>
  )
}

function ProfileCard({ children, title, t, action }) {
  return (
    <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.5rem', boxShadow: t.shadow, marginBottom: 16 }}>
      {title && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', paddingBottom: '1rem', borderBottom: `1px solid ${t.border}` }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: 0 }}>{title}</h3>
          {action}
        </div>
      )}
      {children}
    </div>
  )
}

function ExperienceForm({ t, expForm, onChange, onSubmit, onCancel }) {
  const IS = inputStyle(t)
  const LS = labelStyle(t)
  return (
    <form onSubmit={onSubmit} style={{ background: t.dark ? 'rgba(255,255,255,0.03)' : '#F8F9FA', border: `1px solid ${t.border}`, borderRadius: 10, padding: '1.25rem', marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: '1rem' }}>
        <div><label style={LS}>Company / Employer name *</label><input name="company_name" value={expForm.company_name} onChange={onChange} required style={IS} placeholder="e.g. Acme Corp" /></div>
        <div><label style={LS}>Job title *</label><input name="job_title" value={expForm.job_title} onChange={onChange} required style={IS} placeholder="e.g. Marketing Executive" /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: '1rem' }}>
        <div><label style={LS}>From *</label><input type="date" name="from_date" value={expForm.from_date} onChange={onChange} required style={IS} /></div>
        <div><label style={LS}>To</label><input type="date" name="to_date" value={expForm.to_date} onChange={onChange} style={IS} /></div>
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <label style={LS}>Reporting to / Directly supervised by</label>
        <input name="reported_to" value={expForm.reported_to} onChange={onChange} style={IS} placeholder="e.g. John Smith, Sales Director" />
      </div>
      <div style={{ marginBottom: '1.25rem' }}>
        <label style={LS}>Responsibilities</label>
        <textarea name="responsibilities" value={expForm.responsibilities} onChange={onChange} rows={3} style={{ ...IS, resize: 'vertical' }} placeholder="Key responsibilities and achievements in this role..." />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" style={{ padding: '9px 20px', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'Inter, system-ui' }}>Save experience</button>
        <button type="button" onClick={onCancel} style={{ padding: '9px 20px', background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, color: t.textSub, cursor: 'pointer', fontFamily: 'Inter, system-ui' }}>Cancel</button>
      </div>
    </form>
  )
}

function ExperienceItem({ t, exp, onDelete }) {
  return (
    <div style={{ padding: '1rem 0', borderBottom: `1px solid ${t.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{exp.job_title}</div>
          <div style={{ fontSize: 13, color: '#4F46E5', fontWeight: 600 }}>{exp.company_name}</div>
        </div>
        <button onClick={() => onDelete(exp.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: t.textMuted, lineHeight: 1 }}>×</button>
      </div>
      <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 6 }}>
        {exp.from_date} → {exp.to_date || 'Present'}
        {exp.reported_to && <span> · Reported to: {exp.reported_to}</span>}
      </div>
      {exp.responsibilities && <p style={{ fontSize: 13, color: t.textSub, margin: 0, lineHeight: 1.6 }}>{exp.responsibilities}</p>}
    </div>
  )
}

function MyProfile({ t, profile, pdrs, onRefreshProfile }) {
  const fileRef = useRef()
  const [uploading, setUploading] = useState(false)
  const [msg, setMsg]             = useState('')
  const [saving, setSaving]       = useState(false)
  const [saveMsg, setSaveMsg]     = useState('')
  const [experiences, setExperiences] = useState([])
  const [showExpForm, setShowExpForm] = useState(false)
  const [expForm, setExpForm] = useState({ company_name:'', job_title:'', from_date:'', to_date:'', responsibilities:'', reported_to:'' })
  const [form, setForm] = useState({
    full_name: profile?.full_name||'', phone: profile?.phone||'', dob: profile?.dob||'',
    gender: profile?.gender||'', position: profile?.position||'', department: profile?.department||'',
    years_experience: profile?.years_experience||'', skills: profile?.skills||'',
    address: profile?.address||'', bio: profile?.bio||'', job_description: profile?.job_description||'',
    joined_date: profile?.joined_date||'', current_supervisor: profile?.current_supervisor||'',
  })

  const [pwd, setPwd] = useState({ current: '', newPwd: '', confirm: '' })
  const [pwdMsg, setPwdMsg] = useState('')
  const [savingPwd, setSavingPwd] = useState(false)

  function handle(e)    { const {name,value}=e.target; setForm(prev=>({...prev,[name]:value})) }
  function handleExp(e) { const {name,value}=e.target; setExpForm(prev=>({...prev,[name]:value})) }

  useEffect(() => { fetchExperiences() }, [profile?.id])

  async function fetchExperiences() {
    if (!profile?.id) return
    try {
      const res = await get('/work-experiences')
      setExperiences(res.workExperiences || [])
    } catch (e) { console.error(e.message) }
  }

  async function addExperience(e) {
    e.preventDefault()
    try {
      await post('/work-experiences', {
        company_name: expForm.company_name, job_title: expForm.job_title,
        from_date: expForm.from_date, to_date: expForm.to_date || null,
        responsibilities: expForm.responsibilities, reported_to: expForm.reported_to,
      })
    } catch (err) { alert(`❌ ${err.message}`); return }
    setExpForm({ company_name:'', job_title:'', from_date:'', to_date:'', responsibilities:'', reported_to:'' })
    setShowExpForm(false); fetchExperiences()
  }

  async function deleteExperience(id) {
    try {
      await del(`/work-experiences/${id}`)
    } catch (err) { alert(`❌ ${err.message}`); return }
    fetchExperiences()
  }

  async function handleUpload(e) {
    const file = e.target.files[0]; if (!file) return
    if (file.size > 100*1024) return setMsg('❌ File must be under 100KB.')
    if (!['image/jpeg','image/png','image/webp'].includes(file.type)) return setMsg('❌ Only JPG, PNG or WebP allowed.')
    setUploading(true); setMsg('')
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
      await patch(`/users/${profile.id}`, { avatar_url: dataUrl })
      setMsg('✅ Profile photo updated!'); onRefreshProfile()
    } catch (err) { setMsg(`❌ Upload failed: ${err.message}`) }
    setUploading(false)
  }

  async function handleSave(e) {
    e.preventDefault(); setSaving(true); setSaveMsg('')
    try {
      await patch(`/users/${profile.id}`, {
        full_name: form.full_name, phone: form.phone, dob: form.dob||null, gender: form.gender,
        position: form.position, department: form.department,
        years_experience: form.years_experience ? parseInt(form.years_experience) : null,
        skills: form.skills, address: form.address, bio: form.bio,
        job_description: form.job_description, joined_date: form.joined_date||null,
        current_supervisor: form.current_supervisor,
      })
      setSaveMsg('✅ Profile saved!'); onRefreshProfile()
    } catch (err) { setSaveMsg(`❌ ${err.message}`) }
    setSaving(false)
  }

  async function handlePassword(e) {
    e.preventDefault()
    setPwdMsg('')
    if (!pwd.current) return setPwdMsg('❌ Please enter your current password')
    if (!pwd.newPwd) return setPwdMsg('❌ Please enter a new password')
    if (pwd.newPwd.length < 6) return setPwdMsg('❌ Password must be at least 6 characters')
    if (pwd.newPwd !== pwd.confirm) return setPwdMsg('❌ Passwords do not match')
    setSavingPwd(true)
    try {
      await post('/auth/change-password', { currentPassword: pwd.current, newPassword: pwd.newPwd })
      setPwdMsg('✅ Password changed successfully!'); setPwd({ current: '', newPwd: '', confirm: '' })
    } catch (err) { setPwdMsg(`❌ ${err.message}`) }
    setSavingPwd(false)
  }

  const IS = inputStyle(t)
  const LS = labelStyle(t)
  const completedPdrs = pdrs.filter(p => p.status === 'completed')
  const allGoals = pdrs.flatMap(p => p.pdr_goals || [])
  const avgProgress = allGoals.length ? Math.round(allGoals.reduce((s,g) => s+g.progress, 0) / allGoals.length) : 0

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>My Profile</h1>
      <p style={{ fontSize: 14, color: t.textMuted, margin: '0 0 1.75rem' }}>Manage your personal and professional information</p>
      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20, alignItems: 'start' }}>

        <div>
          <ProfileCard t={t}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}><ProfileAvatar profile={profile} size={100} /></div>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text, marginBottom: 4 }}>{profile?.full_name}</div>
              <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 8 }}>{profile?.email}</div>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <span style={{ fontSize: 11, fontWeight: 600, background: '#4F46E514', color: '#4F46E5', padding: '3px 10px', borderRadius: 99 }}>Employee</span>
                {profile?.position && <span style={{ fontSize: 11, fontWeight: 600, background: '#10B98114', color: '#10B981', padding: '3px 10px', borderRadius: 99 }}>{profile.position}</span>}
              </div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} style={{ display: 'none' }} />
              <div style={{ background: t.dark ? 'rgba(255,255,255,0.04)' : '#F8F9FA', border: `2px dashed ${t.border}`, borderRadius: 10, padding: '1rem', marginBottom: '0.75rem' }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>📷</div>
                <p style={{ fontSize: 12, color: t.textMuted, margin: '0 0 8px' }}>JPG, PNG, WebP · Max 100KB</p>
                <button onClick={() => fileRef.current?.click()} disabled={uploading} style={{ padding: '7px 16px', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'Inter, system-ui' }}>
                  {uploading ? 'Uploading…' : '📤 Upload photo'}
                </button>
              </div>
              {msg && <div style={{ fontSize: 12, padding: '8px 12px', borderRadius: 8, background: msg.startsWith('✅') ? '#ECFDF5' : '#FEF2F2', color: msg.startsWith('✅') ? '#059669' : '#EF4444' }}>{msg}</div>}
            </div>
          </ProfileCard>

          <ProfileCard title="Quick Info" t={t}>
            {[
              { label: 'Years of experience', value: profile?.years_experience ? `${profile.years_experience} years` : '—' },
              { label: 'Date of joining',     value: profile?.joined_date || '—' },
              { label: 'Date of birth',       value: profile?.dob || '—' },
              { label: 'Gender',              value: profile?.gender ? profile.gender.charAt(0).toUpperCase()+profile.gender.slice(1) : '—' },
              { label: 'Phone',               value: profile?.phone || '—' },
              { label: 'Address',             value: profile?.address || '—' },
              { label: 'Reports to',          value: profile?.current_supervisor || '—' },
            ].map(f => (
              <div key={f.label} style={{ display: 'flex', flexDirection: 'column', padding: '8px 0', borderBottom: `1px solid ${t.border}` }}>
                <span style={{ fontSize: 11, color: t.textMuted, fontWeight: 500, marginBottom: 2 }}>{f.label}</span>
                <span style={{ fontSize: 13, color: t.text, fontWeight: 600 }}>{f.value}</span>
              </div>
            ))}
            {profile?.skills && (
              <div style={{ paddingTop: 10 }}>
                <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 500, marginBottom: 6 }}>Skills</div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {profile.skills.split(',').map(s=>s.trim()).filter(Boolean).map(s => (
                    <span key={s} style={{ fontSize: 11, fontWeight: 600, background: '#4F46E514', color: '#4F46E5', padding: '3px 8px', borderRadius: 99 }}>{s}</span>
                  ))}
                </div>
              </div>
            )}
          </ProfileCard>

          <ProfileCard title="Performance Snapshot" t={t}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div style={{ background: t.dark ? 'rgba(255,255,255,0.05)' : '#F8F9FA', borderRadius: 8, padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#4F46E5' }}>{pdrs.length}</div>
                <div style={{ fontSize: 11, color: t.textMuted }}>Total PDRs</div>
              </div>
              <div style={{ background: t.dark ? 'rgba(255,255,255,0.05)' : '#F8F9FA', borderRadius: 8, padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#10B981' }}>{completedPdrs.length}</div>
                <div style={{ fontSize: 11, color: t.textMuted }}>Completed</div>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: t.textSub }}>Avg Goal Progress</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#4F46E5' }}>{avgProgress}%</span>
              </div>
              <div style={{ background: t.border, borderRadius: 99, height: 8 }}>
                <div style={{ width: `${avgProgress}%`, height: '100%', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', borderRadius: 99 }} />
              </div>
            </div>
          </ProfileCard>
        </div>

        <div>
          <form onSubmit={handleSave}>
            <ProfileCard title="Personal Information" t={t}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: '1rem' }}>
                <div><label style={LS}>Full name</label><input name="full_name" value={form.full_name} onChange={handle} style={IS} /></div>
                <div><label style={LS}>Phone number</label><input name="phone" value={form.phone} onChange={handle} placeholder="+91 98765 43210" style={IS} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: '1rem' }}>
                <div><label style={LS}>Date of birth *</label><input type="date" name="dob" value={form.dob} onChange={handle} required style={IS} /></div>
                <div>
                  <label style={LS}>Gender</label>
                  <select name="gender" value={form.gender} onChange={handle} style={IS}>
                    <option value="">Select gender</option>
                    <option value="male">Male</option><option value="female">Female</option>
                    <option value="other">Other</option><option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                </div>
              </div>
              <div><label style={LS}>Address</label><input name="address" value={form.address} onChange={handle} placeholder="City, State, Country" style={IS} /></div>
            </ProfileCard>

            <ProfileCard title="Professional Information" t={t}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: '1rem' }}>
                <div><label style={LS}>Job position / title</label><input name="position" value={form.position} onChange={handle} placeholder="e.g. Senior Developer" style={IS} /></div>
                <div><label style={LS}>Department</label><input name="department" value={form.department} onChange={handle} placeholder="e.g. Engineering" style={IS} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: '1rem' }}>
                <div><label style={LS}>Years of experience</label><input type="number" name="years_experience" value={form.years_experience} onChange={handle} placeholder="e.g. 5" min="0" max="50" style={IS} /></div>
                <div><label style={LS}>Date joined *</label><input type="date" name="joined_date" value={form.joined_date} onChange={handle} required style={IS} /></div>
              </div>
              <div style={{ marginBottom: '1rem' }}><label style={LS}>Currently reporting to / Supervisor</label><input name="current_supervisor" value={form.current_supervisor} onChange={handle} placeholder="e.g. Mohammed Asif, CEO" style={IS} /></div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={LS}>Skills</label>
                <input name="skills" value={form.skills} onChange={handle} placeholder="e.g. React, Node.js, Project Management" style={IS} />
                <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>Separate with commas</div>
              </div>
              <div><label style={LS}>Professional bio</label><textarea name="bio" value={form.bio} onChange={handle} placeholder="Brief description of your professional background..." rows={3} style={{ ...IS, resize: 'vertical' }} /></div>
            </ProfileCard>

            <ProfileCard title="Job Description" t={t}>
              <label style={LS}>My job description</label>
              <textarea name="job_description" value={form.job_description} onChange={handle} placeholder="Describe your role, key responsibilities, and what you do day-to-day..." rows={6} style={{ ...IS, resize: 'vertical' }} />
              <div style={{ fontSize: 12, color: t.textMuted, marginTop: 6 }}>Used during PDR reviews to help your manager understand your scope of work.</div>
            </ProfileCard>

            {saveMsg && (
              <div style={{ fontSize: 13, padding: '12px 16px', borderRadius: 10, marginBottom: 16, background: saveMsg.startsWith('✅') ? '#ECFDF5' : '#FEF2F2', color: saveMsg.startsWith('✅') ? '#059669' : '#EF4444', border: `1px solid ${saveMsg.startsWith('✅') ? '#A7F3D0' : '#FECACA'}` }}>
                {saveMsg}
              </div>
            )}
            <button type="submit" disabled={saving} style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'Inter, system-ui', boxShadow: '0 2px 8px rgba(79,70,229,0.3)', marginBottom: 16 }}>
              {saving ? 'Saving…' : 'Save profile →'}
            </button>
          </form>

          <ProfileCard title="Work Experience" t={t}
            action={!showExpForm && (
              <button onClick={() => setShowExpForm(true)} style={{ fontSize: 12, fontWeight: 600, background: '#4F46E514', border: '1px solid #4F46E530', borderRadius: 8, padding: '6px 14px', color: '#4F46E5', cursor: 'pointer', fontFamily: 'Inter, system-ui' }}>
                + Add experience
              </button>
            )}>
            {showExpForm && <ExperienceForm t={t} expForm={expForm} onChange={handleExp} onSubmit={addExperience} onCancel={() => setShowExpForm(false)} />}
            {experiences.length === 0
              ? <p style={{ fontSize: 13, color: t.textMuted, textAlign: 'center', padding: '1rem 0' }}>No previous work experience added yet.</p>
              : experiences.map(exp => <ExperienceItem key={exp.id} t={t} exp={exp} onDelete={deleteExperience} />)
            }
          </ProfileCard>

          <ProfileCard title="Change Password" t={t}>
            <form onSubmit={handlePassword}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={LS}>Current password</label>
                <input type="password" value={pwd.current} onChange={e => setPwd({ ...pwd, current: e.target.value })} placeholder="••••••••" style={IS} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: '1rem' }}>
                <div>
                  <label style={LS}>New password</label>
                  <input type="password" value={pwd.newPwd} onChange={e => setPwd({ ...pwd, newPwd: e.target.value })} placeholder="At least 6 characters" style={IS} />
                </div>
                <div>
                  <label style={LS}>Confirm new password</label>
                  <input type="password" value={pwd.confirm} onChange={e => setPwd({ ...pwd, confirm: e.target.value })} placeholder="Re-enter new password" style={IS} />
                </div>
              </div>
              {pwdMsg && (
                <div style={{ fontSize: 13, padding: '10px 14px', borderRadius: 10, marginBottom: 12, background: pwdMsg.startsWith('✅') ? '#ECFDF5' : '#FEF2F2', color: pwdMsg.startsWith('✅') ? '#059669' : '#EF4444', border: `1px solid ${pwdMsg.startsWith('✅') ? '#A7F3D0' : '#FECACA'}` }}>
                  {pwdMsg}
                </div>
              )}
              <button type="submit" disabled={savingPwd} style={{ padding: '11px 22px', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#fff', cursor: savingPwd ? 'not-allowed' : 'pointer', fontFamily: 'Inter, system-ui' }}>
                {savingPwd ? 'Updating…' : 'Update password'}
              </button>
            </form>
          </ProfileCard>

          <ProfileCard title="Notifications" t={t}>
            <NotificationSettings t={t} />
          </ProfileCard>
        </div>
      </div>
    </div>
  )
}
